/**
 * settings_controller.js — Settings page wiring (Stimulus).
 *
 * Ports static/js/settings.js to an element-scoped controller. The settings
 * page is a tabbed form (business info, theme, payments, status customization,
 * email templates, danger zone) plus a flatten-across-tabs client-side search.
 *
 * Saving the tab forms is not this controller's job: each form hosts
 * autosave_controller.js. This controller only reacts to its autosave:saved
 * event (navbar business name, and reloading the Assets tab when the industry
 * changes) and resets email templates, which autosave then saves like any
 * other edit.
 *
 * Lifecycle (replaces the two former attachOnce() once-guards —
 * 'settingsListeners' and 'settingsTabFromUrl'):
 *   connect()    binds the delegated document listeners (click / autosave:saved
 *                — the persistent half of the old attachOnce blocks) AND runs the
 *                per-navigation page init that the old code ran from a turbo:load
 *                callback (address autocomplete, active-tab restore, payment
 *                toggles, status customization, settings search, ?tab= deep link).
 *                Because the controller instance owns add+remove, the document
 *                listeners cannot stack across Turbo navigations — exactly what
 *                attachOnce() was hand-rolling around.
 *   disconnect() removes those same document listeners.
 *
 * bootstrap (Bootstrap JS) and AddressAutocomplete are page-scoped *body*
 * scripts; on a Turbo navigation they may not be defined when this controller
 * connects. We defer the page init to turbo:load when bootstrap is missing —
 * the same deferral chart_controller.js uses. The document
 * listeners bind immediately regardless (they don't need bootstrap).
 *
 * Stateless helpers (status customization, entity colors, theme, bug tracker,
 * email-template helpers, settings search) stay as module functions; the ones
 * the template dispatches via data-action="call-fn" stay exposed on window
 * exactly as before. Deleting the business is delete_business_controller.js.
 *
 * data-controller="settings" hosts on #settings-page, which carries the data-*
 * the helpers read (data-region, data-change-theme-url).
 *
 * Template: templates/settings.html
 */

import { Controller } from "@hotwired/stimulus";
import { saveStatusFor, showSaveStatus } from "./_save_status.js";

    // ========================================================================
    // PAYMENT SETTINGS TOGGLES
    // ========================================================================

    function initializePaymentSettings() {
        var toggles = [
            { toggle: 'payment_bank_enabled', settings: 'bankTransferSettings' },
            { toggle: 'payment_cash_enabled', settings: 'cashPaymentSettings' },
            { toggle: 'payment_card_enabled', settings: 'cardPaymentSettings' },
            { toggle: 'payment_stripe_enabled', settings: 'stripePaymentSettings' }
        ];
        toggles.forEach(function (cfg) {
            var toggle = document.getElementById(cfg.toggle);
            var settings = document.getElementById(cfg.settings);
            if (toggle && settings) {
                var handler = function () {
                    settings.style.display = toggle.checked ? 'block' : 'none';
                };
                toggle.addEventListener('change', handler);
                handler(); // Set initial state
            }
        });
    }

    // ========================================================================
    // STATUS CUSTOMIZATION
    // ========================================================================

    var defaultStatusConfig = {
        quotes: {
            pending_quote: {display_name: 'Pending Quote', color: 'info', text_color: 'white'},
            quoted: {display_name: 'Quoted', color: 'warning', text_color: 'dark'},
            accepted: {display_name: 'Accepted', color: 'success', text_color: 'white'},
            cancelled: {display_name: 'Cancelled', color: 'danger', text_color: 'white'},
            archived: {display_name: 'Archived', color: 'secondary', text_color: 'white'}
        },
        jobs: {
            in_progress: {display_name: 'In Progress', color: 'primary', text_color: 'white'},
            on_hold: {display_name: 'On Hold', color: 'warning', text_color: 'dark'},
            completed: {display_name: 'Completed', color: 'success', text_color: 'white'},
            cancelled: {display_name: 'Cancelled', color: 'secondary', text_color: 'white'},
            archived: {display_name: 'Archived', color: 'secondary', text_color: 'white'}
        },
        invoices: {
            draft: {display_name: 'Draft', color: 'secondary', text_color: 'white'},
            pending: {display_name: 'Pending', color: 'warning', text_color: 'dark'},
            unpaid: {display_name: 'Unpaid', color: 'warning', text_color: 'dark'},
            paid: {display_name: 'Paid', color: 'success', text_color: 'white'},
            cancelled: {display_name: 'Cancelled', color: 'secondary', text_color: 'white'},
            archived: {display_name: 'Archived', color: 'secondary', text_color: 'white'}
        }
    };

    var currentStatusConfig = JSON.parse(JSON.stringify(defaultStatusConfig));

    var bootstrapColors = [
        {value: 'primary', name: 'Blue', hex: '#0d6efd'},
        {value: 'secondary', name: 'Gray', hex: '#6c757d'},
        {value: 'success', name: 'Green', hex: '#198754'},
        {value: 'danger', name: 'Red', hex: '#dc3545'},
        {value: 'warning', name: 'Yellow', hex: '#ffc107'},
        {value: 'info', name: 'Cyan', hex: '#0dcaf0'},
        {value: 'orange', name: 'Orange', hex: '#ff8c00'},
        {value: 'dark', name: 'Dark', hex: '#212529'}
    ];

    function loadStatusCustomization() {
        window.api('/api/custom-status-presets', {
            responseType: 'none',
            notifyError: false
        })
            .then(function () {
            })
            .catch(function () {});
        renderStatusCustomization('quotes', 'quoteStatuses');
        renderStatusCustomization('jobs', 'jobStatuses');
        renderStatusCustomization('invoices', 'invoiceStatuses');
        renderStatusFlowDiagram('quotes', 'quoteFlowDiagram');
        renderStatusFlowDiagram('jobs', 'jobFlowDiagram');
        renderStatusFlowDiagram('invoices', 'invoiceFlowDiagram');
    }

    function renderStatusCustomization(entityType, containerId) {
        var container = document.getElementById(containerId);
        if (!container) return;
        container.innerHTML = '';
        var statuses = currentStatusConfig[entityType];
        if (!statuses) return;
        var statusOrder = {
            quotes: ['pending_quote', 'quoted', 'accepted', 'cancelled'],
            jobs: ['ready', 'in_progress', 'on_hold', 'completed', 'cancelled'],
            invoices: ['draft', 'pending', 'unpaid', 'paid', 'cancelled']
        };
        (statusOrder[entityType] || Object.keys(statuses)).forEach(function (key) {
            var cfg = statuses[key];
            if (!cfg) return;
            var div = document.createElement('div');
            div.className = 'status-customization-item';
            div.innerHTML =
                '<div class="status-key-label">' + key.replace(/_/g, ' ') + '</div>' +
                '<div class="status-field"><label>Preview</label>' +
                    '<span class="status-preview bg-' + cfg.color + ' ' + (cfg.text_color === 'white' ? 'text-white' : 'text-dark') + '" id="preview_' + entityType + '_' + key + '">' + cfg.display_name + '</span>' +
                '</div>' +
                '<div class="status-field name-field"><label>Display Name</label>' +
                    '<input type="text" class="form-control form-control-sm" value="' + cfg.display_name + '" onchange="updateStatusConfig(\'' + entityType + '\',\'' + key + '\',\'display_name\',this.value)">' +
                '</div>' +
                '<div class="status-field"><label>Background Color</label>' +
                    '<div class="color-picker-wrapper">' +
                        '<select class="form-select form-select-sm" onchange="updateStatusConfig(\'' + entityType + '\',\'' + key + '\',\'color\',this.value)">' +
                            bootstrapColors.map(function (c) { return '<option value="' + c.value + '" ' + (cfg.color === c.value ? 'selected' : '') + '>' + c.name + '</option>'; }).join('') +
                        '</select>' +
                        '<div class="color-preview" id="color_preview_' + entityType + '_' + key + '" style="background-color:' + getColorHex(cfg.color) + '"></div>' +
                    '</div>' +
                '</div>' +
                '<div class="status-field"><label>Text Color</label>' +
                    '<select class="form-select form-select-sm" onchange="updateStatusConfig(\'' + entityType + '\',\'' + key + '\',\'text_color\',this.value)">' +
                        '<option value="white" ' + (cfg.text_color === 'white' ? 'selected' : '') + '>White</option>' +
                        '<option value="dark" ' + (cfg.text_color === 'dark' ? 'selected' : '') + '>Dark</option>' +
                    '</select>' +
                '</div>';
            container.appendChild(div);
        });
    }

    function renderStatusFlowDiagram(entityType, containerId) {
        var container = document.getElementById(containerId);
        if (!container) return;
        var statuses = currentStatusConfig[entityType];
        if (!statuses) return;
        var flowOrder = {
            quotes: ['pending_quote', 'quoted', 'accepted'],
            jobs: ['ready', 'in_progress', 'completed'],
            invoices: ['draft', 'pending', 'unpaid', 'paid']
        }[entityType] || [];
        var anyStage = {
            quotes: ['cancelled'],
            jobs: ['on_hold', 'cancelled'],
            invoices: ['cancelled']
        }[entityType] || [];

        var wrapper = document.createElement('div');
        wrapper.className = 'flow-wrapper';
        var colMain = document.createElement('div');
        colMain.className = 'flow-col-main';
        var colSide = document.createElement('div');
        colSide.className = 'flow-col-side';

        var flow = document.createElement('div');
        flow.className = 'flow-vertical';
        flowOrder.forEach(function (k, i) {
            var holder = document.createElement('div');
            holder.innerHTML = createFlowStatus(entityType, statuses[k] || {}, k);
            flow.appendChild(holder.firstChild);
            if (i < flowOrder.length - 1) {
                var arrow = document.createElement('div');
                arrow.className = 'flow-arrow-down';
                arrow.textContent = '↓';
                flow.appendChild(arrow);
            }
        });
        colMain.appendChild(flow);

        if (anyStage.length) {
            var header = document.createElement('div');
            header.className = 'flow-side-header';
            header.textContent = 'From any stage';
            colSide.appendChild(header);
            var list = document.createElement('div');
            list.className = 'flow-side-list';
            anyStage.forEach(function (k) {
                var holder = document.createElement('div');
                holder.innerHTML = createFlowStatus(entityType, statuses[k] || {}, k);
                list.appendChild(holder.firstChild);
            });
            colSide.appendChild(list);
        }

        wrapper.appendChild(colMain);
        if (anyStage.length) wrapper.appendChild(colSide);
        container.innerHTML = '';
        container.appendChild(wrapper);
    }

    function createFlowStatus(entityType, cfg, key) {
        var displayName = cfg.display_name || key.replace(/_/g, ' ');
        var color = cfg.color || 'secondary';
        var textColor = cfg.text_color || 'white';
        return '<div id="flow_' + entityType + '_' + key + '" class="flow-status bg-' + color + ' ' + (textColor === 'white' ? 'text-white' : 'text-dark') + '">' + displayName + '</div>';
    }

    function updateStatusConfig(entityType, statusKey, field, value) {
        if (!currentStatusConfig[entityType] || !currentStatusConfig[entityType][statusKey]) return;
        currentStatusConfig[entityType][statusKey][field] = value;
        var preview = document.getElementById('preview_' + entityType + '_' + statusKey);
        if (preview) {
            var cfg = currentStatusConfig[entityType][statusKey];
            preview.textContent = cfg.display_name;
            preview.className = 'status-preview bg-' + cfg.color + ' ' + (cfg.text_color === 'white' ? 'text-white' : 'text-dark');
        }
        var colorBox = document.getElementById('color_preview_' + entityType + '_' + statusKey);
        if (colorBox) {
            colorBox.style.backgroundColor = getColorHex(currentStatusConfig[entityType][statusKey].color);
        }
        var flowBadge = document.getElementById('flow_' + entityType + '_' + statusKey);
        if (flowBadge) {
            var cfg2 = currentStatusConfig[entityType][statusKey];
            flowBadge.textContent = cfg2.display_name;
            flowBadge.className = 'flow-status bg-' + cfg2.color + ' ' + (cfg2.text_color === 'white' ? 'text-white' : 'text-dark');
        }
    }

    function getColorHex(name) {
        var found = bootstrapColors.find(function (c) { return c.value === name; });
        return found ? found.hex : '#6c757d';
    }

    function saveStatusSection(sectionType) {
        var csrfMeta = document.querySelector('meta[name="csrf-token"]');
        var token = csrfMeta ? csrfMeta.getAttribute('content') : '';
        window.api('/api/custom-status-presets', {
            method: 'POST',
            headers: {
                'X-CSRFToken': token
            },
            body: currentStatusConfig,
            responseType: 'none',
            notifyError: false
        })
        .then(function () {
            showNotification('Status customization saved successfully', 'success');
        })
        .catch(function () {
            showNotification('Error saving status customization', 'error');
        });
    }

    function resetStatusCustomization() {
        if (!confirm('Reset all status customizations to defaults?')) return;
        currentStatusConfig = JSON.parse(JSON.stringify(defaultStatusConfig));
        renderStatusCustomization('quotes', 'quoteStatuses');
        renderStatusCustomization('jobs', 'jobStatuses');
        renderStatusCustomization('invoices', 'invoiceStatuses');
        renderStatusFlowDiagram('quotes', 'quoteFlowDiagram');
        renderStatusFlowDiagram('jobs', 'jobFlowDiagram');
        renderStatusFlowDiagram('invoices', 'invoiceFlowDiagram');
    }

    // ========================================================================
    // ENTITY COLORS
    // ========================================================================

    var defaultEntityColors = {
        clients: '#0d6efd',
        quotes: '#ffc107',
        jobs: '#198754',
        invoices: '#fd7e14'
    };

    // Applies a picked colour to the preview and to the page's --entity-color-*
    // variable (declared in base.html), so the change shows without a reload.
    function applyEntityColor(entity, color) {
        var preview = document.getElementById('preview-' + entity);
        if (preview) {
            preview.style.backgroundColor = color;
        }
        document.documentElement.style.setProperty('--entity-color-' + entity, color);
    }

    function changeEntityColor(entity, color) {
        applyEntityColor(entity, color);
        saveEntityColors();
    }

    function saveEntityColors() {
        var marker = saveStatusFor(document.getElementById('entity-color-clients'));
        var colors = {
            clients: (document.getElementById('color-clients') || {}).value || defaultEntityColors.clients,
            quotes: (document.getElementById('color-quotes') || {}).value || defaultEntityColors.quotes,
            jobs: (document.getElementById('color-jobs') || {}).value || defaultEntityColors.jobs,
            invoices: (document.getElementById('color-invoices') || {}).value || defaultEntityColors.invoices
        };
        var csrfMeta = document.querySelector('meta[name="csrf-token"]');
        var token = csrfMeta ? csrfMeta.getAttribute('content') : '';

        showSaveStatus(marker, 'saving');
        window.api('/settings/api/entity-colors', {
            method: 'POST',
            headers: {
                'X-CSRFToken': token
            },
            body: { colors: colors },
            notifyError: false
        })
        .then(function (data) {
            if (data.success) {
                showSaveStatus(marker, 'saved');
            } else {
                showSaveStatus(marker, 'failed', { retry: saveEntityColors });
                showNotification(data.message || 'Failed to save colors', 'error');
            }
        })
        .catch(function () {
            showSaveStatus(marker, 'failed', { retry: saveEntityColors });
            showNotification('Error saving entity colors', 'error');
        });
    }

    function resetEntityColors() {
        if (!confirm('Reset all entity colors to defaults?')) return;
        Object.keys(defaultEntityColors).forEach(function (entity) {
            var input = document.getElementById('color-' + entity);
            if (input) input.value = defaultEntityColors[entity];
            applyEntityColor(entity, defaultEntityColors[entity]);
        });
        saveEntityColors();
    }

    // ========================================================================
    // THEME SELECTION
    // ========================================================================

    function selectTheme(themeId) {
        var page = document.getElementById('settings-page');
        var changeThemeUrl = page ? page.dataset.changeThemeUrl : '/theme/change';
        var themeCard = document.querySelector('[data-theme-id="' + themeId + '"]');
        if (!themeCard) return;
        var originalContent = themeCard.innerHTML;

        themeCard.innerHTML = '<div class="text-center"><i class="bi bi-hourglass-split"></i> Applying...</div>';
        themeCard.style.pointerEvents = 'none';

        var csrfMeta = document.querySelector('meta[name="csrf-token"]');
        var token = csrfMeta ? csrfMeta.getAttribute('content') : '';

        window.api(changeThemeUrl, {
            method: 'POST',
            headers: {
                'X-CSRFToken': token
            },
            body: { theme_id: themeId },
            notifyError: false
        })
        .then(function (data) {
            if (data.success) {
                var current = document.getElementById('currentThemeName');
                if (current) current.textContent = data.theme_name || themeId;

                document.querySelectorAll('.theme-card').forEach(function (card) {
                    card.classList.remove('theme-card-active');
                    var badge = card.querySelector('.theme-active-badge');
                    if (badge) badge.remove();
                });

                themeCard.classList.add('theme-card-active');
                var badge = document.createElement('div');
                badge.className = 'theme-active-badge';
                badge.innerHTML = '<i class="bi bi-check-circle-fill"></i> Active';
                themeCard.appendChild(badge);

                setTimeout(function () { window.location.reload(); }, 400);
            } else {
                showNotification(data.message || 'Error changing theme', 'error');
                themeCard.innerHTML = originalContent;
                themeCard.style.pointerEvents = 'auto';
            }
        })
        .catch(function () {
            showNotification('Error changing theme', 'error');
            themeCard.innerHTML = originalContent;
            themeCard.style.pointerEvents = 'auto';
        });
    }

    // ========================================================================
    // BUG TRACKER TOGGLE
    // ========================================================================

    function toggleBugTracker() {
        var csrfMeta = document.querySelector('meta[name="csrf-token"]');
        var token = csrfMeta ? csrfMeta.getAttribute('content') : '';

        fetch('/dev/toggle-bug-tracker', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': token
            }
        })
        .then(function (response) { return response.json(); })
        .then(function (data) {
            if (data.success) {
                var statusLabel = document.getElementById('bugTrackerStatus');
                if (statusLabel) {
                    statusLabel.textContent = data.hidden ? 'Hidden' : 'Visible';
                }

                var badges = [
                    document.getElementById('navbarEnvBadge'),
                    document.getElementById('navbarEnvBadgeMobile')
                ];
                badges.forEach(function (badge) {
                    if (badge) {
                        badge.style.setProperty('display', data.hidden ? 'none' : 'inline-block', 'important');
                    }
                });

                var bugTabs = document.getElementById('feedbackTabs');
                if (bugTabs) {
                    bugTabs.style.setProperty('display', data.hidden ? 'none' : 'flex', 'important');
                }

                showNotification(data.message, 'success');
            } else {
                showNotification(data.message || 'Failed to toggle bug tracker', 'error');
            }
        })
        .catch(function () {
            showNotification('Error toggling bug tracker', 'error');
        });
    }

    // ========================================================================
    // EMAIL TEMPLATE RESET
    // The bubbling change hands the reset to the form's autosave controller.
    // ========================================================================

    function resetEmailTemplate(textareaId) {
        var textarea = document.getElementById(textareaId);
        if (!textarea) return;
        textarea.value = textarea.dataset.default;
        textarea.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function resetTestDatabase() {
        if (!confirm('Reset database to test state? This will delete all current data for this business and restore the Thomas Carpentry test dataset.')) return;

        // window.api, not raw fetch — same as every other call on this page. It sends
        // the auth cookie and CSRF token and sets X-Requested-With/Accept, so an auth
        // failure comes back as JSON rather than a 303 to the login page. The old code
        // called r.json() on whatever came back, so ANY non-JSON response (a redirect
        // body, an nginx 502) collapsed into the same useless 'Error resetting test
        // database' toast with the real reason thrown away.
        window.api('/dev/database/reset-test-database', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            notifyError: false
        })
        .then(function(data) {
            if (data && data.success) {
                showNotification('Test database reset successfully!', 'success');
                setTimeout(function() { window.location.reload(); }, 1500);
            } else {
                showNotification((data && data.message) || 'Reset failed', 'error');
            }
        })
        .catch(function(err) {
            // Surface what actually went wrong instead of a generic string.
            var msg = (err && err.message) ? err.message : 'Error resetting test database';
            console.error('resetTestDatabase failed:', err);
            showNotification(msg, 'error');
        });
    }

    // Fires one real notification across the business (bell + toast + push),
    // so the toast the sender sees arrives over Mercure, not from this handler.
    // The response therefore reports reach — recipients and registered devices —
    // rather than claiming delivery the fire-and-forget dispatch cannot confirm.
    function sendTestPush(el) {
        var button = el && el.tagName === 'BUTTON' ? el : null;
        if (button) button.disabled = true;

        window.api('/api/v1/push/send-test', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            notifyError: false
        })
        .then(function (data) {
            var message = (data && data.message) || 'Test notification sent';
            showNotification(message, data && data.success ? 'success' : 'warning');
        })
        .catch(function (err) {
            var msg = (err && err.message) ? err.message : 'Error sending test notification';
            console.error('sendTestPush failed:', err);
            showNotification(msg, 'error');
        })
        .finally(function () {
            if (button) button.disabled = false;
        });
    }

    // ========================================================================
    // URL-driven tab activation: /settings?tab=<id> opens that tab on load.
    // (Former attachOnce('settingsTabFromUrl') — its turbo:load re-run is now
    //  folded into connect(), which runs on every navigation.)
    // ========================================================================
    function activateTabFromUrl() {
        if (!document.getElementById('settingsTabs')) return;
        var tabParam = new URLSearchParams(window.location.search).get('tab');
        if (!tabParam) return;
        var btn = document.getElementById(tabParam + '-tab');
        if (btn && window.bootstrap && window.bootstrap.Tab) {
            new window.bootstrap.Tab(btn).show();
        }
    }

    // ========================================================================
    // MOBILE SECTION LIST
    //
    // On a phone, settings.css shows the tab bar as a full-width list of
    // sections and hides the panes until `.section-open` is on `.settings-tabs`,
    // like a phone's own Settings app. Tapping a section opens it; the back
    // link returns to the list. On desktop the class changes nothing.
    // ========================================================================
    function setSectionOpen(open) {
        var tabs = document.querySelector('.settings-tabs');
        if (!tabs) return;
        tabs.classList.toggle('section-open', open);
        // The breakpoint matches the mobile block in settings.css.
        if (window.matchMedia('(max-width: 768px)').matches) {
            tabs.scrollIntoView({ block: 'start' });
        }
    }

    // Returns true when the click was a section open or close, so the caller
    // stops looking for other targets.
    function toggleMobileSection(e) {
        if (e.target.closest('#settingsTabs .nav-link')) {
            setSectionOpen(true);
            return true;
        }
        if (e.target.closest('.settings-section-back')) {
            setSectionOpen(false);
            return true;
        }
        return false;
    }

    // ========================================================================
    // FLATTEN-ACROSS-TABS SETTINGS SEARCH
    //
    // Filters the static settings form client-side. While a query is active we add
    // `.searching` to `.settings-tabs` (CSS force-shows every pane and hides the tab
    // bar), then hide non-matching `.setting-item` rows, empty `.settings-category`
    // sections, and panes with no surviving content. Each visible section gets a
    // breadcrumb chip naming its origin tab. Matching units are harvested from the
    // live DOM, so there is no per-row index to maintain (SSOT: the rendered form).
    //
    // Tabs whose partials lack `.setting-item` structure (Theme, Region, Development)
    // fall back to whole-pane text matching, so they stay findable by name.
    // ========================================================================

    function initSettingsSearch() {
        var input = document.getElementById('settings-search');
        var tabsWrap = document.querySelector('.settings-tabs');
        if (!input || !tabsWrap) return;

        var emptyMsg = document.getElementById('settings-search-empty');

        function tabNameFor(pane) {
            var btn = document.getElementById(pane.id + '-tab');
            return btn ? btn.textContent.trim() : '';
        }

        function resetFlags() {
            tabsWrap.querySelectorAll('.search-hidden').forEach(function (el) {
                el.classList.remove('search-hidden');
            });
            tabsWrap.querySelectorAll('.settings-search-crumb').forEach(function (crumb) {
                crumb.remove();
            });
        }

        function addCrumb(block, label) {
            var crumb = document.createElement('div');
            crumb.className = 'settings-search-crumb';
            crumb.textContent = label;
            block.insertBefore(crumb, block.firstChild);
        }

        function runSearch(raw) {
            var query = (raw || '').trim().toLowerCase();
            resetFlags();

            if (!query) {
                tabsWrap.classList.remove('searching');
                if (emptyMsg) emptyMsg.hidden = true;
                return;
            }
            tabsWrap.classList.add('searching');

            var anyVisible = false;

            tabsWrap.querySelectorAll('.tab-pane').forEach(function (pane) {
                var tabName = tabNameFor(pane);
                var tabMatches = tabName.toLowerCase().indexOf(query) !== -1;

                var categories = pane.querySelectorAll('.settings-category');
                var blocks = categories.length
                    ? Array.prototype.slice.call(categories)
                    : [pane];
                var paneVisible = false;

                blocks.forEach(function (block) {
                    var heading = block.querySelector('h3, h4, h5');
                    var headingText = heading ? heading.textContent : '';
                    var items = block.querySelectorAll('.setting-item');
                    var blockVisible = false;

                    if (items.length) {
                        var headingMatches = tabMatches ||
                            headingText.toLowerCase().indexOf(query) !== -1;
                        items.forEach(function (item) {
                            var text = (tabName + ' ' + headingText + ' ' + item.textContent).toLowerCase();
                            item.querySelectorAll('[placeholder]').forEach(function (el) {
                                text += ' ' + el.getAttribute('placeholder').toLowerCase();
                            });
                            if (headingMatches || text.indexOf(query) !== -1) {
                                blockVisible = true;
                            } else {
                                item.classList.add('search-hidden');
                            }
                        });
                    } else {
                        var blockText = (tabName + ' ' + block.textContent).toLowerCase();
                        blockVisible = tabMatches || blockText.indexOf(query) !== -1;
                    }

                    if (blockVisible) {
                        paneVisible = true;
                        addCrumb(block, tabName);
                    } else if (block !== pane) {
                        block.classList.add('search-hidden');
                    }
                });

                if (paneVisible) {
                    anyVisible = true;
                } else {
                    pane.classList.add('search-hidden');
                }
            });

            if (emptyMsg) emptyMsg.hidden = anyVisible;
        }

        if (!input._searchWired) {
            input._searchWired = true;
            input.addEventListener('input', function () {
                runSearch(input.value);
            });
            input.addEventListener('keydown', function (e) {
                if (e.key === 'Escape') {
                    input.value = '';
                    runSearch('');
                    input.blur();
                }
            });
        }

        // Re-apply any standing query after a Turbo re-render.
        if (input.value) runSearch(input.value);
    }

    // ========================================================================
    // PER-NAVIGATION PAGE INITIALIZATION
    // (Former turbo:load callback inside attachOnce('settingsListeners').)
    // ========================================================================

    function initPage() {
        var page = document.getElementById('settings-page');
        if (!page) return;

        var region = page.dataset.region || '';

        // ---- Business address autocomplete ----
        var businessAddressSearch = document.getElementById('business_address_search');
        if (businessAddressSearch && typeof AddressAutocomplete !== 'undefined') {
            new AddressAutocomplete(businessAddressSearch, {
                country: region,
                onSelect: function (suggestion) {
                    document.getElementById('address_street').value = suggestion.street || '';
                    document.getElementById('address_city').value = suggestion.city || '';
                    document.getElementById('address_state').value = suggestion.state || '';
                    document.getElementById('address_postcode').value = suggestion.postcode || '';
                    document.getElementById('address_latitude').value = suggestion.latitude || '';
                    document.getElementById('address_longitude').value = suggestion.longitude || '';
                    var countryField = document.getElementById('address_country');
                    if (countryField) countryField.value = suggestion.country || '';
                    // Filling fields by script fires no events; this one lets the
                    // Business Info form's autosave pick the new address up.
                    document.getElementById('address_street')
                        .dispatchEvent(new Event('change', { bubbles: true }));
                    // Pre-select the Region dropdown from the address country
                    // (country IS the region code). Only when a matching option
                    // exists; the user confirms by saving the Region tab. The
                    // bubbling change keeps the VAT-field visibility in step and
                    // marks the Region tab form unsaved.
                    var regionSelect = document.getElementById('region_select');
                    if (regionSelect && suggestion.country) {
                        var match = Array.prototype.some.call(
                            regionSelect.options, function (o) { return o.value === suggestion.country; });
                        if (match && regionSelect.value !== suggestion.country) {
                            regionSelect.value = suggestion.country;
                            regionSelect.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                }
            });
        }

        // ---- Remember active tab ----
        var settingsTabs = document.getElementById('settingsTabs');
        if (settingsTabs) {
            var urlHash = window.location.hash.substring(1);
            var savedTab = localStorage.getItem('settings-active-tab');
            var activeTab = urlHash || savedTab || 'business';

            // A link to a named section (#hash or ?tab=) opens it directly
            // on a phone instead of landing on the section list.
            var tabParam = new URLSearchParams(window.location.search).get('tab');
            document.querySelector('.settings-tabs')
                .classList.toggle('section-open', Boolean(urlHash || tabParam));

            var tabButton = document.querySelector('#' + activeTab + '-tab');
            if (tabButton) {
                var tab = new bootstrap.Tab(tabButton);
                tab.show();
            }

            // Only attach the shown.bs.tab handler once per element
            if (!settingsTabs._settingsTabHandler) {
                settingsTabs._settingsTabHandler = function (e) {
                    var activeTabId = e.target.getAttribute('aria-controls');
                    if (activeTabId) {
                        localStorage.setItem('settings-active-tab', activeTabId);
                        // Keep history.state: it holds Turbo's restoration id, and
                        // a null state makes Turbo ignore Back to this page.
                        history.replaceState(history.state, '', '#' + activeTabId);
                    }
                };
                settingsTabs.addEventListener('shown.bs.tab', settingsTabs._settingsTabHandler);
            }
        }

        // ---- Payment settings toggles ----
        initializePaymentSettings();

        // ---- Status customization UI ----
        loadStatusCustomization();

        // ---- Flatten-across-tabs settings search ----
        initSettingsSearch();

        // ---- URL-driven (?tab=) deep link ----
        activateTabFromUrl();
    }

    // ========================================================================
    // CONTROLLER
    // ========================================================================

    // The industry as a Business Info post carries it: the trade, or Other's text.
    function industryIn(formData) {
        return (formData.get('industry') || '') + '|' + (formData.get('industry_other') || '');
    }

    export default class extends Controller {
        connect() {
            this._bindListeners();
            var industryField = document.getElementById('industry');
            this._industry = industryField && industryField.form
                ? industryIn(new FormData(industryField.form)) : null;

            // bootstrap (Bootstrap JS) + AddressAutocomplete are page body
            // scripts; on a Turbo nav they may not be defined yet. Defer the
            // page init to turbo:load when bootstrap is missing.
            if (typeof bootstrap !== 'undefined') {
                initPage();
                return;
            }
            this._onLoad = function () { initPage(); };
            document.addEventListener('turbo:load', this._onLoad, { once: true });
        }

        disconnect() {
            if (this._onLoad) {
                document.removeEventListener('turbo:load', this._onLoad);
                this._onLoad = null;
            }
            if (this._listeners) {
                this._listeners.forEach(function (pair) {
                    document.removeEventListener(pair[0], pair[1]);
                });
                this._listeners = null;
            }
        }

        _bindListeners() {
            // Delegated on document because the persistent listeners from the
            // old attachOnce('settingsListeners') block fire for elements that
            // are re-rendered on each navigation. The controller instance owns
            // add/remove, so they don't stack across navigations (the
            // attachOnce replacement).
            this._listeners = [
                ['click', this.onClick.bind(this)],
                ['autosave:saved', this.onSaved.bind(this)],
            ];
            this._listeners.forEach(function (pair) {
                document.addEventListener(pair[0], pair[1]);
            });
        }

        // Mobile section list, then email template reset buttons
        onClick(e) {
            if (toggleMobileSection(e)) return;
            if (e.target.closest('#quote_template_reset_btn')) {
                resetEmailTemplate('quote_email_template');
            } else if (e.target.closest('#invoice_template_reset_btn')) {
                resetEmailTemplate('invoice_email_template');
            } else if (e.target.closest('#quote_sms_template_reset_btn')) {
                resetEmailTemplate('quote_sms_template');
            } else if (e.target.closest('#invoice_sms_template_reset_btn')) {
                resetEmailTemplate('invoice_sms_template');
            }
        }

        // A saved business name updates the navbar display name without a reload
        onSaved(e) {
            this._reloadAssetTypesIfIndustryChanged(e.detail.formData);
            var newName = e.detail.formData.get('business_name');
            if (!newName) return;
            var navSmall = document.querySelector('nav .dropdown-toggle small');
            var navHeader = document.querySelector('.dropdown-menu .dropdown-header');
            if (navSmall) navSmall.textContent = (newName.length > 20 ? newName.slice(0, 20) + '...' : newName);
            if (navHeader) navHeader.textContent = newName;
        }

        // The Assets tab shows the industry and its trade's ready-made types,
        // but industry is saved from Business Info as JSON with nothing to
        // patch. When a save changes it, reload that tab's frame at once, so it
        // is current before the user opens it (settings/_asset_types_frame.html).
        _reloadAssetTypesIfIndustryChanged(formData) {
            if (!formData.has('industry')) return;
            var industry = industryIn(formData);
            if (industry === this._industry) return;
            this._industry = industry;
            var frame = document.getElementById('settings-asset-types');
            if (!frame) return;
            if (frame.src) frame.reload();
            else frame.src = frame.dataset.src;
        }
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js)
    // and the inline onchange="" handlers in rendered status-customization markup.
    window.changeEntityColor = changeEntityColor;
    window.resetEntityColors = resetEntityColors;
    window.resetStatusCustomization = resetStatusCustomization;
    window.resetTestDatabase = resetTestDatabase;
    window.saveEntityColors = saveEntityColors;
    window.saveStatusSection = saveStatusSection;
    window.selectTheme = selectTheme;
    window.sendTestPush = sendTestPush;
    window.toggleBugTracker = toggleBugTracker;
    window.updateStatusConfig = updateStatusConfig;
